import React from 'react';
import { useTts } from '../../contexts/TtsContext';
import { SpeakerWaveIcon, SpeakerXMarkIcon } from '@heroicons/react/24/solid';
import { useTranslation } from '../../i18n/useTranslation';

interface TTSToggleButtonProps {
  /** null = Read Aloud not available for this location; button renders disabled. */
  bookPageTextId: string | null;
  className?: string;
  /** Tooltip/aria text shown when disabled (no BookPageText exists). */
  unavailableReason?: string;
}

/**
 * TTS Toggle Button
 * Activates Read Aloud for a specific BookPageText (chapter/page).
 * Always rendered in the header; shows a disabled state with a tooltip
 * when the backend has no BookPageText for the current location.
 * 44x44px touch target for mobile.
 */
export const TTSToggleButton: React.FC<TTSToggleButtonProps> = ({
  bookPageTextId,
  className = '',
  unavailableReason,
}) => {
  const { t } = useTranslation();
  const { isActive, activateTts, playbackState } = useTts();

  const isLoading = playbackState === 'loading';
  const isUnavailable = !bookPageTextId;
  const disabled = isUnavailable || isActive || isLoading;

  const handleClick = async () => {
    if (!bookPageTextId || isActive) return;
    await activateTts(bookPageTextId);
  };

  const defaultUnavailableReason = unavailableReason ?? t('App.Reader.TTS.UnavailableReason');
  const label = isUnavailable ? defaultUnavailableReason : t('App.Reader.TTS.ActivateLabel');
  const baseColor = isUnavailable
    ? 'bg-neutral-300 hover:bg-neutral-300'
    : 'bg-primary-600 hover:bg-primary-700 disabled:bg-neutral-400';

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={`w-11 h-11 flex items-center justify-center rounded-full disabled:cursor-not-allowed transition-colors shadow-md ${baseColor} ${className}`}
      aria-label={isUnavailable ? t('App.Reader.TTS.Unavailable') : t('App.Reader.TTS.ToggleLabel')}
      title={label}
    >
      {isLoading ? (
        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
      ) : isUnavailable ? (
        <SpeakerXMarkIcon className="w-6 h-6 text-white" />
      ) : (
        <SpeakerWaveIcon className="w-6 h-6 text-white" />
      )}
    </button>
  );
};
